import { inject, Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { NavController } from '@ionic/angular';
import { TranslocoService } from '@ngneat/transloco';

@Injectable({
  providedIn: 'root',
})
export class TPVService {
  private functions = inject(Functions);
  private navController = inject(NavController);
  private transloco = inject(TranslocoService);
  /** Sent to the backend so it can pick the right URLOK/URLKO and, on iOS, build the bridge page */
  private platform = Capacitor.getPlatform();
  /** Sent so Redsys shows its payment page in the language the customer is using */
  private get language() {
    return this.transloco.getActiveLang();
  }

  public createRedsysPaymentIntent(inscriptionId: string) {
    const callable = httpsCallable(this.functions, 'createRedsysPaymentIntent');
    return callable({ inscriptionId, platform: this.platform, language: this.language }).then((result: any) => {
      this.redirectToRedsysPayment(result.data);
    });
  }

  public retryBookingPayment(
    bookingId: string,
    extras?: {
      observations?: string;
      requestInvoice?: boolean;
      invoiceData?: Record<string, string>;
    },
  ) {
    const callable = httpsCallable(this.functions, 'retryBookingPayment');
    // Devuelve el resultado: `{ settled: true }` cuando el saldo a favor del equipo cubría la
    // reserva entera y no ha hecho falta abrir la pasarela.
    return callable({ bookingId, ...extras, platform: this.platform, language: this.language }).then((result: any) =>
      this.redirectToRedsysPayment(result.data),
    );
  }

  public createPaymentIntent(data: {
    inscriptionId: string;
    editionId: string;
    extraServices: { id: string; quantity: number; price: number }[];
    rate?: string | null;
    appType: 'maroc' | 'tunisie';
    type: 'REDSYS' | 'BANK';
    paymentOption?: 'all' | 'registration';
  }) {
    const callable = httpsCallable(this.functions, 'inscriptionPayment');
    return callable({ ...data, platform: this.platform, language: this.language }).then((result: any) => {
      result.data.type === 'REDSYS'
        ? this.redirectToRedsysPayment(result.data)
        : this.navController.navigateRoot('/payment/transfer/' + result.data.inscriptionId);
    });
  }

  private createHiddenForm(
    parameters: Record<string, any>,
    url: string,
    target = '_top',
    method = 'POST',
  ) {
    const form = document.createElement('form');
    form.setAttribute('method', method);
    form.setAttribute('target', target);
    form.setAttribute('action', url);
    for (const [key, value] of Object.entries(parameters)) {
      if (value) {
        form.appendChild(this.createHiddenInput(key, value));
      }
    }
    return form;
  }

  private createHiddenInput(name: string, value: string) {
    const el = document.createElement('input');
    el.setAttribute('type', 'hidden');
    el.setAttribute('name', name);
    el.setAttribute('value', value);
    return el;
  }

  /**
   * Abre la pasarela de Redsys con el formulario que ha firmado el servidor.
   *
   * Devuelve `{ settled: true }` cuando no hay nada que cobrar porque el saldo a favor del equipo
   * cubría la reserva entera: en ese caso el servidor ya la ha dejado pagada y no hay pasarela que
   * abrir. El camino de Redsys (Android, web y el puente de iOS) no cambia en nada.
   */
  public async redirectToRedsysPayment(functionResponse: any): Promise<{ settled: boolean; discount?: number }> {
    if (functionResponse?.type === 'SETTLED') {
      return { settled: true, discount: functionResponse.discount };
    }

    if (!functionResponse?.url) {
      throw new Error('INVALID_PAYMENT_RESPONSE');
    }

    // iOS: the 3DS step of Redsys leaves *.redsys.es (issuer ACS) and the WKWebView kicks that
    // navigation out to Safari as a GET, losing the POST. Run the whole payment in
    // SFSafariViewController instead; Redsys returns to a universal link that reopens the app.
    if (this.platform === 'ios' && functionResponse.bridgeUrl) {
      await Browser.open({ url: functionResponse.bridgeUrl });
      return { settled: false };
    }

    // Android and web: unchanged, POST straight from the app.
    const form = this.createHiddenForm(
      {
        Ds_SignatureVersion: functionResponse['Ds_SignatureVersion'],
        Ds_MerchantParameters: functionResponse['Ds_MerchantParameters'],
        Ds_Signature: functionResponse['Ds_Signature'],
      },
      functionResponse['url'],
    );

    document.body.appendChild(form);
    form.submit();
    return { settled: false };
  }
}
